import { useAuth } from '@/loaders/Auth'
import { useMutation } from '@tanstack/react-query'

/**
 * Hook to initiate the Google Ads OAuth consent flow.
 *
 * It asks the HoneyGrid Google OAuth service for an authorization URL and then
 * redirects the browser to that URL. The service is expected to be reachable at
 * the address defined in the `VITE_OAUTH_API_URL` env variable which defaults
 * to `http://localhost:8976` during local development.
 *
 * Usage example:
 * ```ts
 * const { mutate: beginOauth } = useBeginGoogleAdsOAuth()
 * beginOauth(workspaceId)
 * ```
 */
export const useBeginGoogleAdsOAuth = () => {
	const { userId } = useAuth()

	return useMutation({
		mutationFn: async (workspaceId: string) => {
			if (!workspaceId) throw new Error('workspaceId is required')
			if (!userId) throw new Error('Logged-in user id is missing')

			const oauthApiUrl = import.meta.env.VITE_OAUTH_API_URL || 'http://localhost:8976'
			const query = `${oauthApiUrl}/?workspaceId=${workspaceId}&userId=${userId}`

			const response = await fetch(query)
			if (!response.ok) {
				throw new Error(`OAuth service error: ${response.status}`)
			}

			const data = (await response.json()) as { url: string }
			if (!data?.url) throw new Error('OAuth URL missing in response')

			return data.url
		},
		onSuccess: (authorizationUrl) => {
			// Navigate the current tab to Google consent screen
			window.location.assign(authorizationUrl)
		},
	})
}
